import React, { useEffect, useState } from 'react';
import { List, notification, Spin } from 'antd';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpSection } from 'components/customAntd/DLS/OpSection/OpSection';
import { OpSteps } from 'components/customAntd/DLS/OpSteps/OpSteps';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { Role } from 'types/roleTypes';
import { AppDispatch, RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { postRequest } from 'api/apiClient';
import axios from 'axios';
import { LoadingOutlined } from '@ant-design/icons';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useTranslation } from 'react-i18next';
import { fetchIntegrationStatus } from 'store/slices/integrationSlice';

interface ConnectFormData {
    msiAltaToken: string;
    msiAltaTokenExpUtc: string;
}

interface OrgFormData {
    allowMsiAltaLogin?: number;
    roleId?: number;
    msiAltaUserId?: number;
    importUsers?: number;
    msiAltaOrgId?: number;
    syncHours?: number;
}

interface IntegrationInstallProps {
    setIsStepsOpen: (open: boolean) => void;
    setIsInstalled: (open: boolean) => void;
}

const IntegrationInstall: React.FC<IntegrationInstallProps> = ({ setIsStepsOpen, setIsInstalled }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const dispatch: AppDispatch = useDispatch();

    const [current, setCurrent] = useState(0);
    const [orgOptions, setOrgOptions] = useState<{ value: string; label: string, userId: number }[]>([]);
    const [entryIdOptions, setEntryIdOptions] = useState<{ value: string; label: string }[]>([]);
    const [apiHost, setApiHost] = useState('https://api.openpath.com');

    // eslint-disable-next-line
    const [roles, setRoles] = useState<Role[]>([]);

    const [connectFormData, setConnectFormData] = useState<ConnectFormData>({ msiAltaToken: '', msiAltaTokenExpUtc: '' });
    const [orgFormData, setOrgFormData] = useState<OrgFormData | undefined>(undefined);;

    // eslint-disable-next-line
    const [isImportUsersEnabled, setIsImportUsersEnabled] = useState(false);
    const [isGuestPassGenerationEnabled, setIsGuestPassGenerationEnabled] = useState(false);

    const [loading, setLoading] = useState(false);

    const confirmModal = useConfirmModal();

    const [connectForm] = OpForm.useForm();
    const [orgForm] = OpForm.useForm();
    const [visitorForm] = OpForm.useForm();

    const { t } = useTranslation();

    // Monitor the update of connectFormData
    useEffect(() => {
        console.log("Updated connectFormData:", connectFormData);
    }, [connectFormData]);

    // Monitor the update of orgFormData
    useEffect(() => {
        console.log("Updated orgFormData:", orgFormData);
    }, [orgFormData]);

    const next = async () => {
        if (current === 0) {
            try {
                await connectForm.validateFields();
                connectForm.submit();
            } catch (error) {
                return; // Do not proceed if validation fails
            }
        } else if (current === 1) {
            try {
                await orgForm.validateFields();
                orgForm.submit();
            } catch (error) {
                return; // Do not proceed if validation fails
            }
        } else if (current === 2) {
            try {
                await visitorForm.validateFields();
                visitorForm.submit();
            } catch (error) {
                return; // Do not proceed if validation fails
            }
        }
    };

    const prev = () => {
        setCurrent(current - 1);
    };

    const handleCancel = () => {
        confirmModal({
            title: t('integration.confirmCancel.title'),
            content: t('integration.confirmCancel.message'),
            okText: t('integration.confirmCancel.ok'),
            cancelText: t('integration.confirmCancel.cancel'),
            onOk: () => {
                setIsStepsOpen(false); // Go back to the main menu
            },
        });
    };

    // eslint-disable-next-line
    const handleBackToMain = () => {
        setIsStepsOpen(false); // Go back to the main menu
    };

    // eslint-disable-next-line
    const handleImportUsersChange = (checked: boolean) => {
        setIsImportUsersEnabled(checked);
        if (checked) {
            console.log("orgForm.getFieldValue('syncHours')", orgForm.getFieldValue('syncHours'));
            if (orgForm.getFieldValue('syncHours') === undefined) {
                orgForm.setFieldsValue({ syncHours: 24 });
            }
        }
    };

    const handleGuestPassEnabled = (checked: boolean) => {
        setIsGuestPassGenerationEnabled(checked);
        if (checked) {
            if (visitorForm.getFieldValue('guestPassBeforeMins') === undefined) {
                visitorForm.setFieldsValue({ guestPassBeforeMins: -1 });
            }
            if (visitorForm.getFieldValue('guestPassAfterMins') === undefined) {
                visitorForm.setFieldsValue({ guestPassAfterMins: -1 });
            }
        }
    };

    const handleConnectSubmit = (async ({ values }: IOnSubmitArgs) => {
        setLoading(true);
        try {
            const response = await axios.post(`${apiHost}/auth/login`, {
                email: values.email,
                password: values.password
            });
            const orgList = response.data.data.tokenScopeList
                .filter((scope: any) => scope.org.id !== null)
                .map((scope: any) => ({
                    value: scope.org.id,
                    label: scope.org.name,
                    userId: scope.user.id
                }));
            setOrgOptions(orgList);
            setConnectFormData({ msiAltaToken: response.data.data.token, msiAltaTokenExpUtc: response.data.data.expiresAt });
            setCurrent(1); // Proceed to Org step after successful connection
            if (orgList.length > 0) {
                setOrgFormData({
                    msiAltaOrgId: orgList[0].value,
                    msiAltaUserId: orgList[0].userId,
                });
            }
        } catch (error) {
            notification.error({
                message: t('integration.notification.connectionFailed'),
                description: t('integration.notification.connectionError'),
                placement: 'bottomRight'
            });
        } finally {
            setLoading(false);
        }
    });

    const handleOrgSubmit = (async ({ values }: IOnSubmitArgs) => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiHost}/orgs/${orgForm.getFieldValue('msiAltaOrgId')}/entries`, {
                headers: {
                    Authorization: `${connectFormData.msiAltaToken}`,
                },
            });
            const defaultEntryList = response.data.data
                .map((defaultEntry: any) => ({
                    value: defaultEntry.id,
                    label: defaultEntry.name,
                }));
            setEntryIdOptions(defaultEntryList);

            const formattedValues = {
                allowMsiAltaLogin: values.allowMsiAltaLogin && values.allowMsiAltaLogin === true ? 1 : 0,
                roleId: values.roleId,
                importUsers: values.importUsers && values.importUsers === true ? 1 : 0,
                msiAltaOrgId: values.msiAltaOrgId,
                msiAltaUserId: orgForm.getFieldValue('msiAltaUserId'),
                syncHours: values.syncHours ? values.syncHours : 0,
            };
            setOrgFormData(formattedValues);
            setCurrent(2);
        } catch (error) {
            notification.error({
                message: t('integration.notification.connectionFailed'),
                description: t('integration.notification.connectionError'),
                placement: 'bottomRight'
            });
        } finally {
            setLoading(false);
        }
    });

    const handleVisitorSubmit = (async ({ values }: IOnSubmitArgs) => {
        setLoading(true);

        // Get all current form values to prevent field loss
        const allValues = visitorForm.getFieldsValue();

        const formattedValues = {
            msiAltaEntrieIds: allValues.msiAltaEntrieIds || values.msiAltaEntrieIds,
            guestPassEnabled: allValues.guestPassEnabled === true ? 1 : 0,
            guestPassBeforeMins: allValues.guestPassBeforeMins === undefined ? -1 : allValues.guestPassBeforeMins,
            guestPassAfterMins: allValues.guestPassAfterMins === undefined ? -1 : allValues.guestPassAfterMins,
        };

        try {
            const combinedData = {
                ...connectFormData,
                ...orgFormData,
                ...formattedValues,
                msiAltaApiHost: apiHost
            };
            console.log(combinedData);
            await postRequest(`/orgs/${orgId}/integrationMsiAlta`, combinedData);
            // setCurrent(3);
            setIsStepsOpen(false);

            await dispatch(fetchIntegrationStatus({ orgId }));

            notification.success({
                message: t('notification.success'),
                description: t('integration.notification.integrateSuccess'),
                placement: 'bottomRight',
            });
            setIsInstalled(true);
        } catch {
            notification.error({
                message: t('notification.error'),
                description: t('integration.notification.saveError'),
                placement: 'bottomRight',
            });
        } finally {
            setLoading(false);
        }
    });

    const steps = [
        {
            title: t('integration.install.steps.connect'),
            content: (
                <OpSection>
                    <OpForm
                        form={connectForm}
                        onSubmit={handleConnectSubmit}
                        initialValues={{
                            msiAltaApiHost: "https://api.openpath.com"
                        }}
                        defaultButtons={false}
                        hasError={false}
                    >
                        <OpForm.Input
                            name="email"
                            label={t('integration.install.form.email')}
                            rules={[{ required: true, message: t('integration.install.form.emailRequired') }]}
                            placeholder={t('integration.install.form.emailPlaceholder')}
                        />

                        <OpForm.PasswordInput
                            name="password"
                            label={t('integration.install.form.password')}
                            rules={[{ required: true, message: t('integration.install.form.passwordRequired') }]}
                            placeholder={t('integration.install.form.passwordPlaceholder')}
                        />
                        <OpForm.Select
                            name="msiAltaApiHost"
                            label={t('msiAltaApiHost')}
                            rules={[{ required: true, message: t('msiAltaApiHostRequired') }]}
                            placeholder={t('msiAltaApiHostPlaceholder')}
                            options={[
                                { label: 'US', value: 'https://api.openpath.com' },
                                { label: 'Europe', value: 'https://api.eu.openpath.com' }
                            ]}
                            onChange={(value) => {
                                if (typeof value === 'string') {
                                    setApiHost(value);
                                }
                            }}
                        />
                    </OpForm>
                </OpSection>
            ),
        },
        {
            title: t('integration.install.steps.org'),
            content: (
                <OpForm
                    form={orgForm}
                    onSubmit={handleOrgSubmit}
                    defaultButtons={false}
                    hasError={false}
                    initialValues={orgFormData}
                >
                    <List
                        header={t('integration.install.form.organizationTitle')}
                        dataSource={[
                            { name: 'msiAltaOrgId', label: t('integration.install.form.orgLabel') }
                        ]}
                        bordered
                        style={{ marginBottom: '16px' }}
                        renderItem={item => (
                            <List.Item>
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <OpForm.Select
                                        name={item.name}
                                        label={item.label}
                                        placeholder={t('integration.install.form.orgPlaceholder')}
                                        options={orgOptions}
                                        rules={[{ required: true, message: t('integration.install.form.orgRequired') }]}
                                        onChange={(value) => {
                                            const selectedOrg = orgOptions.find((org) => org.value === value);
                                            if (selectedOrg) {
                                                orgForm.setFieldsValue({
                                                    msiAltaUserId: selectedOrg.userId, // Store the userId in the form
                                                });
                                            }
                                        }}
                                    />
                                </div>
                            </List.Item>
                        )}
                    />
                </OpForm>
            ),
        },
        {
            title: t('integration.install.steps.visitor'),
            content: (
                <OpForm
                    form={visitorForm}
                    onSubmit={handleVisitorSubmit}
                    defaultButtons={false}
                    hasError={false}
                >
                    <List
                        header={t('integration.install.form.visitorTitle')}
                        dataSource={[
                            { label: t('integration.install.form.guestPasses'), name: 'guestPassEnabled' },
                            ...(isGuestPassGenerationEnabled ? [
                                { label: t('integration.install.form.defaultEntries'), name: 'msiAltaEntrieIds' }
                            ] : []),
                        ]}
                        bordered
                        renderItem={item => (
                            <List.Item style={{ width: '100%' }}>
                                {item.name === 'guestPassBeforeMins' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <OpForm.Select
                                            name={item.name}
                                            label={item.label}
                                            placeholder={t('integration.install.form.interval.placeholder')}
                                            options={[
                                                { value: -1, label: t('integration.install.form.interval.manual') },
                                                { value: 0, label: t('integration.install.form.interval.atSignIn') },
                                                { value: 10, label: t('integration.install.form.interval.before10') },
                                                { value: 60, label: t('integration.install.form.interval.before60') },
                                                { value: 1440, label: t('integration.install.form.interval.before1440') },
                                            ]}
                                        />
                                    </div>
                                ) : item.name === 'guestPassAfterMins' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <OpForm.Select
                                            name={item.name}
                                            label={item.label}
                                            placeholder={t('integration.install.form.interval.placeholder')}
                                            options={[
                                                { value: -1, label: t('integration.install.form.interval.manual') },
                                                { value: 10, label: t('integration.install.form.interval.after10') },
                                                { value: 60, label: t('integration.install.form.interval.after60') },
                                                { value: 1440, label: t('integration.install.form.interval.after1440') },
                                            ]}
                                        />
                                    </div>
                                ) : item.name === 'msiAltaEntrieIds' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <OpForm.Select
                                            name={item.name}
                                            label={item.label}
                                            placeholder={t('integration.install.form.entryPlaceholder')}
                                            mode="multiple"
                                            options={entryIdOptions}
                                        />
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <OpForm.Switch
                                            name={item.name}
                                            onChange={handleGuestPassEnabled}
                                        />
                                        <span style={{ marginLeft: 8 }}>{item.label}</span>
                                    </div>
                                )}
                            </List.Item>
                        )}
                    />
                </OpForm>
            ),
        },
    ];

    const items = steps.map((item) => ({ key: item.title, title: item.title }));

    return (
        <>
            {(loading) ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin indicator={<LoadingOutlined spin />} />
                </div>
            ) : (
                <>
                    <OpSteps current={current} items={items} />

                    {
                        steps.map((item, index) => (
                            <div key={item.title} style={{ display: index === current ? 'block' : 'none', marginTop: 16 }}>
                                {item.content}
                            </div>
                        ))
                    }

                    <div style={{ marginTop: 24 }}>
                        {current < steps.length - 1 && (
                            <OpButton type="primary" onClick={() => next()}>
                                {t('integration.install.button.next')}
                            </OpButton>
                        )}
                        {current === steps.length - 1 && (
                            <OpButton type="primary" htmlType="submit" onClick={() => next()}>
                                {t('integration.install.button.done')}
                            </OpButton>
                        )}
                        {current === 0 && (
                            <OpButton style={{ margin: '0 8px' }} onClick={handleCancel}>
                                {t('integration.install.button.cancel')}
                            </OpButton>
                        )}
                        {current > 0 ? (
                            <OpButton style={{ margin: '0 8px' }} onClick={() => prev()}>
                                {t('integration.install.button.previous')}
                            </OpButton>
                        ) : null}
                    </div>
                </>
            )}
        </>
    );
};

export default IntegrationInstall;


